/** SVG 导出：展开图案，viewBox 使用毫米单位，可无缝缩放。 */
import type { Project } from '../domain/types';
import { computeUnfold, paperGeometry } from '../domain/folds/geometry';
import { getFold } from '../domain/folds/definitions';
import { multiPolyToPaths } from '../domain/geometry/boolean';
import { boundsOf, polygonToPath } from '../domain/geometry/polygon';

export interface SvgExportOptions {
  /** 是否带白色背景（默认透明）。 */
  background?: boolean;
  /** 描边宽度（mm），0 表示不描边。 */
  strokeMm?: number;
}

export function projectToSvg(project: Project, options: SvgExportOptions = {}): string {
  const fold = getFold(project.foldId);
  const geo = paperGeometry(project.foldId, project.finishedSize);
  const result = computeUnfold(project.foldId, project.finishedSize, project.cuts);
  const b = boundsOf(geo.paper);
  const pad = 2;
  const w = b.maxX - b.minX + pad * 2;
  const h = b.maxY - b.minY + pad * 2;
  const d = polygonToPath(geo.paper) + (result.holes.length ? multiPolyToPaths(result.holes) : '');
  const stroke = options.strokeMm ?? 0;

  const bg = options.background
    ? `<rect x="${b.minX - pad}" y="${b.minY - pad}" width="${w}" height="${h}" fill="#ffffff"/>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w.toFixed(2)}mm" height="${h.toFixed(2)}mm" viewBox="${(b.minX - pad).toFixed(3)} ${(b.minY - pad).toFixed(3)} ${w.toFixed(3)} ${h.toFixed(3)}">
<title>${escapeXml(fold.name)}剪纸图案</title>
<desc>由 A4 剪纸与窗花生成器导出。折法：${escapeXml(fold.name)}（${fold.layers} 层）；成品尺寸语义：${project.finishedSize.kind}。</desc>
${bg}<path d="${d}" fill="${project.paperColor}" fill-rule="evenodd"${stroke > 0 ? ` stroke="#000000" stroke-width="${stroke}"` : ''}/>
</svg>
`;
}

export function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => {
    switch (c) {
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '&':
        return '&amp;';
      case '"':
        return '&quot;';
      default:
        return '&apos;';
    }
  });
}

/** 展开图案的毫米包围盒，供 PNG 按物理尺寸换算像素。 */
export function unfoldedSizeMm(project: Project): { widthMm: number; heightMm: number } {
  const geo = paperGeometry(project.foldId, project.finishedSize);
  const b = boundsOf(geo.paper);
  return { widthMm: b.maxX - b.minX + 4, heightMm: b.maxY - b.minY + 4 };
}